import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useAdminOverview } from '@/hooks/useAdmin';
import { useAuth } from '@/auth/AuthContext';
import { Loading, ErrorBanner, EmptyState } from '@/components/common/States';
import { fmtDuration, relTime, shortId } from '@/lib/format';
import { isSystemAdmin, type AdminOverview } from '@/types';

const GREEN = '#54f0a8';
const AMBER = '#ffc24b';
const RED = '#ff5d6c';

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'ok' | 'warn' | 'loss' }) {
  const color = tone === 'loss' ? RED : tone === 'warn' ? AMBER : tone === 'ok' ? GREEN : undefined;
  return (
    <div className="card kpi">
      <div className="k-label">{label}</div>
      <div className="k-value" style={{ fontSize: 26, color }}>{value}</div>
      {sub && <div className="mono" style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="panel" style={{ padding: '16px 16px 8px' }}>
      <div className="eyebrow" style={{ marginBottom: 12 }}>{title}</div>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">{children as any}</ResponsiveContainer>
      </div>
    </div>
  );
}

const axis = { stroke: 'rgba(150,180,220,0.25)', fontSize: 11, fontFamily: 'var(--font-mono, monospace)' };
const tip = {
  contentStyle: { background: '#0b0f17', border: '1px solid rgba(150,180,220,0.18)', borderRadius: 8, fontSize: 12 },
  labelStyle: { color: '#5d6b80' },
};

function outcomeBar(o: AdminOverview) {
  const total = o.sessions_total || 1;
  const seg = [
    { k: 'completed', n: o.sessions_completed, c: GREEN },
    { k: 'active', n: o.sessions_active, c: '#4aa8ff' },
    { k: 'error', n: o.sessions_error, c: RED },
    { k: 'pending', n: Math.max(0, o.sessions_total - o.sessions_completed - o.sessions_active - o.sessions_error), c: '#5d6b80' },
  ].filter((s) => s.n > 0);
  return (
    <div>
      <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: 'rgba(150,180,220,0.08)' }}>
        {seg.map((s) => <div key={s.k} title={`${s.k}: ${s.n}`} style={{ width: `${(s.n / total) * 100}%`, background: s.c }} />)}
      </div>
      <div className="row wrap" style={{ gap: 14, marginTop: 12 }}>
        {seg.map((s) => (
          <span key={s.k} className="mono" style={{ fontSize: 12, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <i style={{ width: 8, height: 8, borderRadius: 2, background: s.c }} />{s.k} · {s.n}
          </span>
        ))}
      </div>
    </div>
  );
}

export function Platform() {
  const { user: me } = useAuth();
  const { data: o, isLoading, error } = useAdminOverview();

  if (!isSystemAdmin(me)) {
    return <EmptyState title="System admins only" hint="Platform analytics are restricted to system administrators." />;
  }
  if (isLoading) return <Loading label="Crunching platform metrics" />;
  if (error) return <ErrorBanner error={error} />;
  if (!o) return <EmptyState title="No data" />;

  const errPct = (o.error_rate * 100).toFixed(1);

  return (
    <div className="fade-up">
      <div className="page-head">
        <div className="eyebrow">platform · system admin</div>
        <div className="title">Platform overview</div>
        <div className="sub">How Arc is being used across every organization · updated {relTime(o.generated_at)}</div>
      </div>

      <div className="kpi-grid" style={{ marginBottom: 22 }}>
        <Kpi label="tests run" value={o.sessions_total.toLocaleString()} sub={`${o.sessions_active} running now`} />
        <Kpi label="credits used" value={fmtDuration(o.credits_consumed)} sub="all time" tone="ok" />
        <Kpi label="credits · 24h" value={fmtDuration(o.credits_last_24h)} sub="last 24 hours" />
        <Kpi label="error rate" value={`${errPct}%`} sub={`${o.sessions_error} failed`} tone={o.error_rate > 0.15 ? 'loss' : o.error_rate > 0.05 ? 'warn' : 'ok'} />
        <Kpi label="agents online" value={`${o.agents_online}/${o.agents}`} sub="live now / total" tone={o.agents_online > 0 ? 'ok' : undefined} />
        <Kpi label="users" value={o.users.toLocaleString()} sub={`${o.orgs} companies`} />
        <Kpi label="credits outstanding" value={fmtDuration(o.credits_outstanding)} sub="unspent balance" />
        <Kpi label="avg test" value={fmtDuration(Math.round(o.avg_duration_s))} sub="per completed run" />
      </div>

      <div className="row wrap" style={{ gap: 18, marginBottom: 22 }}>
        <div style={{ flex: '1 1 340px', minWidth: 300 }}>
          <ChartCard title="credit usage · 14 days">
            <AreaChart data={o.timeseries} margin={{ top: 4, right: 8, left: -14, bottom: 0 }}>
              <defs>
                <linearGradient id="gcred" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor={GREEN} stopOpacity={0.5} />
                  <stop offset="1" stopColor={GREEN} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(150,180,220,0.08)" vertical={false} />
              <XAxis dataKey="day" tick={axis} tickFormatter={(d) => String(d).slice(5)} axisLine={false} tickLine={false} />
              <YAxis tick={axis} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => (v >= 3600 ? `${Math.round(v / 3600)}h` : `${Math.round(v / 60)}m`)} />
              <Tooltip {...tip} formatter={(v: number) => [fmtDuration(v), 'credits']} />
              <Area type="monotone" dataKey="credits" stroke={GREEN} strokeWidth={2} fill="url(#gcred)" />
            </AreaChart>
          </ChartCard>
        </div>
        <div style={{ flex: '1 1 340px', minWidth: 300 }}>
          <ChartCard title="tests per day · 14 days">
            <BarChart data={o.timeseries} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
              <CartesianGrid stroke="rgba(150,180,220,0.08)" vertical={false} />
              <XAxis dataKey="day" tick={axis} tickFormatter={(d) => String(d).slice(5)} axisLine={false} tickLine={false} />
              <YAxis tick={axis} axisLine={false} tickLine={false} width={32} allowDecimals={false} />
              <Tooltip {...tip} formatter={(v: number) => [v, 'tests']} cursor={{ fill: 'rgba(150,180,220,0.06)' }} />
              <Bar dataKey="sessions" fill={AMBER} radius={[3, 3, 0, 0]} maxBarSize={26} />
            </BarChart>
          </ChartCard>
        </div>
      </div>

      <div className="row wrap" style={{ gap: 18, marginBottom: 22, alignItems: 'stretch' }}>
        <div className="card" style={{ flex: '1 1 340px', minWidth: 300 }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>test outcomes</div>
          {outcomeBar(o)}
        </div>
        <div className="card" style={{ flex: '1 1 340px', minWidth: 300 }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>error taxonomy</div>
          {o.by_error.length === 0 ? (
            <div className="mono" style={{ color: 'var(--signal)', fontSize: 13 }}>✓ No failed tests recorded.</div>
          ) : (
            <div className="stack" style={{ gap: 8 }}>
              {o.by_error.map((e) => (
                <div key={e.label} className="spread mono" style={{ fontSize: 13 }}>
                  <span style={{ color: 'var(--loss)' }}>{e.label}</span>
                  <span className="muted">{e.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="row wrap" style={{ gap: 18 }}>
        <div className="panel" style={{ flex: '1 1 380px', minWidth: 320 }}>
          <div className="eyebrow" style={{ padding: '14px 16px 4px' }}>top companies · by usage</div>
          <table className="table">
            <thead><tr><th>Company</th><th className="right">Credits</th><th className="right">Tests</th></tr></thead>
            <tbody>
              {o.top_orgs.length === 0 ? (
                <tr><td colSpan={3} className="dim">No usage yet</td></tr>
              ) : o.top_orgs.map((org) => (
                <tr key={org.org_id ?? org.name}>
                  <td className="mono">{org.name}</td>
                  <td className="right mono signal">{fmtDuration(org.credits)}</td>
                  <td className="right mono">{org.sessions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="panel" style={{ flex: '1 1 380px', minWidth: 320 }}>
          <div className="eyebrow" style={{ padding: '14px 16px 4px' }}>top users · by usage</div>
          <table className="table">
            <thead><tr><th>User</th><th>Company</th><th className="right">Credits</th></tr></thead>
            <tbody>
              {o.top_users.length === 0 ? (
                <tr><td colSpan={3} className="dim">No usage yet</td></tr>
              ) : o.top_users.map((u) => (
                <tr key={u.user_id}>
                  <td className="mono">{u.email}</td>
                  <td className="dim mono" style={{ fontSize: 12 }}>{u.org ?? '—'}</td>
                  <td className="right mono signal">{fmtDuration(u.credits)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {o.recent_errors.length > 0 && (
        <div className="panel" style={{ marginTop: 22 }}>
          <div className="eyebrow" style={{ padding: '14px 16px 4px' }}>recent failures</div>
          <table className="table">
            <thead><tr><th>Session</th><th>Reason</th><th>Company</th><th>When</th></tr></thead>
            <tbody>
              {o.recent_errors.map((e) => (
                <tr key={e.id}>
                  <td className="mono dim">{shortId(e.id)}</td>
                  <td className="mono" style={{ color: 'var(--loss)' }}>{e.reason ?? 'unknown'}</td>
                  <td className="dim mono" style={{ fontSize: 12 }}>{e.org ?? '—'}</td>
                  <td className="dim">{e.at ? relTime(e.at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
